import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faBan, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import './VoidInvoiceModal.css';

/**
 * Two-step confirmation for voiding an invoice.
 *
 * Voiding is irreversible: it creates a full reversing note (credit note for an
 * outgoing invoice, debit note for an incoming one) and marks the invoice as no
 * longer usable. The two deliberate steps guard against accidental clicks.
 *
 * Props:
 *  - isOpen: bool
 *  - invoice: the invoice row object
 *  - noteType: 'credit' | 'debit' (which reversing note will be created)
 *  - isProcessing: bool (disables buttons + shows spinner while the API runs)
 *  - onConfirm(): called on the final confirmation
 *  - onCancel(): called to dismiss
 */
const VoidInvoiceModal = ({
  isOpen,
  invoice,
  noteType = 'credit',
  isProcessing = false,
  onConfirm,
  onCancel,
}) => {
  const [step, setStep] = useState(1);

  // Always restart at step 1 whenever the modal (re)opens
  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  if (!isOpen || !invoice) return null;

  const noteLabel = noteType === 'debit' ? 'debit note' : 'credit note';
  const invoiceRef =
    invoice.invoice_number ||
    invoice.supplier_invoice_number ||
    invoice.hms_expense_number ||
    `#${invoice.id}`;

  const handleOverlayClick = () => {
    if (!isProcessing) onCancel();
  };

  return (
    <div className="void-invoice-modal-overlay" onClick={handleOverlayClick}>
      <div className="void-invoice-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`void-invoice-modal-header ${step === 2 ? 'is-final' : ''}`}>
          <FontAwesomeIcon icon={step === 1 ? faExclamationTriangle : faBan} />
          <h3 className="void-invoice-modal-title">Void Invoice {invoiceRef}</h3>
        </div>

        <div className="void-invoice-modal-body">
          {step === 1 ? (
            <div className="void-invoice-warning-banner">
              <FontAwesomeIcon icon={faExclamationTriangle} className="void-invoice-warning-icon" />
              <div className="void-invoice-warning-text">
                <strong>This action is not reversible.</strong>
                <p>Are you sure you want to void this invoice?</p>
              </div>
            </div>
          ) : (
            <>
              <div className="void-invoice-impact">
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                <div className="void-invoice-impact-text">
                  <p>
                    This will create a <strong>{noteLabel}</strong> for this invoice, and the
                    invoice will no longer be usable or active.
                  </p>
                  <p className="void-invoice-impact-sub">
                    The {noteLabel} mirrors the full invoice amount and is submitted for financial
                    approval.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="void-invoice-modal-footer">
          {step === 1 ? (
            <>
              <button
                type="button"
                className="void-invoice-btn-secondary"
                onClick={onCancel}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="void-invoice-btn-primary"
                onClick={() => setStep(2)}
                disabled={isProcessing}
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="void-invoice-btn-secondary"
                onClick={() => setStep(1)}
                disabled={isProcessing}
              >
                Back
              </button>
              <button
                type="button"
                className="void-invoice-btn-danger"
                onClick={onConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? 'Voiding…' : 'Void Invoice'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoidInvoiceModal;
